import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logger from '../utils/logger';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [sessionData, setSessionData] = useState(null);
  const [reportStatus, setReportStatus] = useState('pending');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  // Poll for report availability after payment is confirmed
  const pollReportStatus = async (paymentId, attempts = 0) => {
    const maxAttempts = 20; // Poll for up to 40 seconds
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setReportStatus('timeout');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}/report`,
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to check report status');
      }

      const data = await response.json();
      
      if (data.status === 'ready') {
        setReportData(data);
        setReportStatus('ready');
        return;
      } else if (data.status === 'pending') {
        // Continue polling
        setTimeout(() => pollReportStatus(paymentId, attempts + 1), pollInterval);
      } else {
        setReportStatus('error');
      }
    } catch (error) {
      logger.error('Error checking report status:', error);
      // Continue polling on error
      setTimeout(() => pollReportStatus(paymentId, attempts + 1), pollInterval);
    }
  };

  // Poll payment status to ensure completion
  useEffect(() => {
    if (!sessionId) {
      setError('No payment session found');
      setPaymentStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000; // 2 seconds

      if (attempts >= maxAttempts) {
        setError('Payment verification timed out. Please check your email for confirmation.');
        setPaymentStatus('timeout');
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/status/${sessionId}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();
        setSessionData(data);
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('completed');
          
          // Start polling for report if payment_id available
          const paymentId = data.metadata?.payment_id;
          if (paymentId) {
            pollReportStatus(paymentId);
          }
          return;
        } else if (data.status === 'expired') {
          setError('Payment session expired. Please try again.');
          setPaymentStatus('expired');
          return;
        }

        // Continue polling if payment is still pending
        setPaymentStatus('processing');
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        
      } catch (error) {
        logger.error('Error checking payment status:', error);
        setError('Error verifying payment. Please contact support if you were charged.');
        setPaymentStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

const renderContent = () => {
    switch (paymentStatus) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {paymentStatus === 'checking' ? 'Verifying Payment...' : 'Processing Payment...'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Please wait while we confirm your payment. This may take a few moments.
            </p>
          </div>
        );
      
      case 'completed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Payment Successful!
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Thank you for your purchase. Your professional ESA report has been generated.
            </p>

            {sessionData && (
              <div className="bg-emerald-50 dark:bg-emerald-900 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-400 mb-3">Payment Details</h3>
                <div className="text-left space-y-2 text-sm text-gray-900 dark:text-gray-100">
                  <p><span className="font-medium">Amount:</span> ${(sessionData.amount_total / 100).toFixed(2)} {sessionData.currency?.toUpperCase()}</p>
                  <p><span className="font-medium">Payment ID:</span> {sessionId}</p>
                  <p><span className="font-medium">Status:</span> <span className="text-emerald-600 dark:text-emerald-400">Paid</span></p>
                  {sessionData.metadata?.assessment_id && (
                    <p><span className="font-medium">Assessment ID:</span> {sessionData.metadata.assessment_id}</p>
                  )}
                </div>
              </div>
            )}

            {/* Report Download Section */}
            {reportStatus === 'ready' && reportData && (
              <div className="bg-blue-50 dark:bg-blue-900 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <h3 className="font-bold text-blue-800 dark:text-blue-300 text-lg">Your Report is Ready!</h3>
                </div>
                <p className="text-blue-700 dark:text-blue-300 mb-4 text-sm">
                  {reportData.email_sent_to && (
                    <>Report has been sent to <span className="font-semibold">{reportData.email_sent_to}</span></>
                  )}
                </p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${reportData.download_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                  Download Report PDF
                </a>
              </div>
            )}

            {reportStatus === 'pending' && (
              <div className="bg-yellow-50 dark:bg-yellow-900 rounded-lg p-6 mb-8 text-yellow-900 dark:text-yellow-300">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-5 h-5 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                  <h3 className="font-semibold">Generating Your Report...</h3>
                </div>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Your professional report is being prepared. This usually takes 10-30 seconds.
                </p>
              </div>
            )}

            {reportStatus === 'timeout' && (
              <div className="bg-orange-50 dark:bg-orange-900 rounded-lg p-6 mb-8 text-orange-900 dark:text-orange-400">
                <h3 className="font-semibold mb-3">Report Generation in Progress</h3>
                <p className="text-orange-700 dark:text-orange-300 text-sm">
                  Your report is taking longer than expected. You'll receive it via email at {sessionData?.customer_email || 'your registered email'} within a few minutes.
                </p>
              </div>
            )}

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-800 dark:text-gray-300 mb-3">What's Included</h3>
              <div className="text-left space-y-3 text-sm text-gray-900 dark:text-gray-100">
                <div className="flex items-start gap-3">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                  <p>Professional energy assessment report with detailed analysis</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                  <p>Energy savings recommendations and ROI calculations</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                  <p>Implementation roadmap and priority actions</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                  <p>Report sent to your email for easy access</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Link
                to="/"
                className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold inline-block transition-colors"
              >
                Return to Home
              </Link>
              <div>
                <Link
                  to="/sample-esa-report"
                  className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium"
                >
                  View Sample Report Structure →
                </Link>
              </div>
            </div>
          </div>
        );

      case 'error':
      case 'expired':
      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Payment Verification Issue
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              {error || 'There was an issue verifying your payment.'}
            </p>

            <div className="bg-amber-50 dark:bg-amber-900 border border-amber-200 dark:border-amber-700 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-amber-800 dark:text-amber-400 mb-2">Need Help?</h3>
              <p className="text-amber-700 dark:text-amber-300 text-sm">
                If you were charged, please contact our support team with your payment details.
                We'll resolve this quickly and ensure you receive your report.
              </p>
            </div>

            <div className="space-y-4">
              <a
                href="mailto:info@relyability.ai?subject=Payment Verification Issue"
                className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold inline-block transition-colors"
              >
                Contact Support
              </a>
              <div>
                <Link
                  to="/"
                  className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium"
                >
                  ← Return to Home
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-2xl mx-auto px-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Secure payment processed by Stripe | Questions? Contact{' '}
            <a href="mailto:info@relyability.ai" className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300">
              info@relyability.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;